import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { computeScores } from "@/lib/score";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, TrendingUp } from "lucide-react";

export function TradeOffExplorer({ alternatives, criteria, weights, onRefine, saving }) {
  const { t } = useTranslation();
  const [w, setW] = useState(() => {
    const init = {};
    criteria.forEach((c) => (init[c.id] = weights[c.id] ?? 3));
    return init;
  });

  const scores = useMemo(() => computeScores(alternatives, w), [alternatives, w]);
  const ranked = useMemo(
    () => [...alternatives].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)),
    [alternatives, scores]
  );

  const dirty = criteria.some((c) => (weights[c.id] ?? 3) !== w[c.id]);

  return (
    <div className="card-crisp p-6 sm:p-8" data-testid="trade-off-explorer">
      <div className="mb-6 flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-ikb" />
        <h3 className="font-display text-lg font-bold">{t("tradeoff.title")}</h3>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            {t("tradeoff.hint")}
          </p>
          {criteria.map((c) => (
            <div key={c.id} data-testid={`slider-row-${c.id}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">{c.name}</span>
                <span className="font-mono text-xs font-semibold text-ikb">{w[c.id]}</span>
              </div>
              <Slider
                value={[w[c.id]]}
                min={0}
                max={5}
                step={1}
                onValueChange={(v) => setW((p) => ({ ...p, [c.id]: v[0] }))}
                data-testid={`priority-slider-${c.id}`}
              />
            </div>
          ))}
        </div>

        <div>
          <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" /> {t("tradeoff.liveRanking")}
          </div>
          <div className="space-y-2">
            <AnimatePresence>
              {ranked.map((a, i) => (
                <motion.div
                  key={a.id}
                  layout
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  className={`flex items-center gap-3 rounded-md border p-3 ${
                    i === 0 ? "border-ikb bg-ikb/5" : "border-border bg-card"
                  }`}
                  data-testid={`ranked-option-${a.id}`}
                >
                  <span className="font-display text-lg font-extrabold text-muted-foreground">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{a.name}</p>
                  </div>
                  <div className="w-24">
                    <div className="mb-1 flex justify-end">
                      <span className="font-mono text-sm font-bold">{scores[a.id]}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                      <motion.div
                        className="h-full bg-ikb"
                        animate={{ width: `${scores[a.id]}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <Button
            className="mt-5 w-full bg-foreground text-background hover:bg-foreground/90"
            disabled={!dirty || saving}
            onClick={() => onRefine(w)}
            data-testid="save-priorities-btn"
          >
            {saving ? t("tradeoff.recalculating") : dirty ? t("tradeoff.apply") : t("tradeoff.applied")}
          </Button>
        </div>
      </div>
    </div>
  );
}
